import { useLocation, useNavigate } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  EmployeeSelectorDocument,
  GetCompetenciesDocument,
  TrainingRequestsDataCollectionDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { encodeActivity } from 'modules/core/lib/activities/utils'
import i18n from 'modules/core/lib/i18n'

import { DEFAULT_DATA_COLLECTION_STORAGE } from 'modules/trainings/lib/collections'

import { buildMapVariables } from './mapVariables'
import { getPresets } from './usePresets'
import { useActions } from './useActions'
import { useExportAction } from './useActions'
import { getVisualizations } from './visualizations'

interface Props {
  onReject: (id: string) => void
  settingsEnabled: boolean
  canCreate: boolean
}

export const RequestsDataCollection = ({ onReject, settingsEnabled, canCreate }: Props) => {
  const client = useApolloClient()
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const { itemActions } = useActions({ onReject })
  const { secondaryActions, mappedVariablesRef } = useExportAction()

  type EmployeeFilterNode = { id: string; fullName: string }

  const employeeFilterSource = useGraphqlDataSource(
    {
      document: EmployeeSelectorDocument,
      fetchPolicy: 'cache-first',
      paginationType: 'infinite-scroll',
      perPage: 25,
      mapVariables: ({ search }) => ({
        companyId,
        active: true,
        onlyManagers: false,
        search: search ?? '',
        excludeIds: [],
      }),
      mapData: (data): EmployeeFilterNode[] =>
        data.employees.employeeNamesConnection.edges.map((edge) => ({
          id: edge.node.id,
          fullName: edge.node.fullName,
        })),
      mapPagination: (data) => {
        const pageInfo = data.employees.employeeNamesConnection.pageInfo
        return {
          total: 0,
          cursor: pageInfo.endCursor ?? '0',
          hasNextPage: pageInfo.hasNextPage ?? false,
        }
      },
    },
    [companyId]
  )

  const mapVariables = buildMapVariables(companyId)

  const source = useGraphqlDataCollectionSource({
    totalItemSummary:true, 
    document: TrainingRequestsDataCollectionDocument,
    mapData: (data) => {
      return (data?.trainings?.trainingRequestsConnection?.nodes ?? []).filter(Boolean)
    },
    mapPagination: (data) => ({
      total: data?.trainings?.trainingRequestsConnection?.totalCount ?? 0,
    }),
    mapVariables: (input) => {
      const variables = mapVariables(input)
      // Update ref for export
      mappedVariablesRef.current = {
        search: variables.search,
        filter: variables.filter,
        sortOrder: variables.sortOrder,
      }
      return variables
    },
    search: { enabled: true },
    sortings: {
      createdAt: {
        label: i18n.t('trainings.training_requests.my_requests.datatable.columns.request_date'),
      },
    },
    defaultSortings: {
      field: 'createdAt',
      order: 'desc',
    },
    presets: getPresets(),
    filters: {
      authorId: {
        type: 'in',
        label: i18n.t('trainings.training_requests.requests.datatable.filters.applicant'),
        options: {
          source: employeeFilterSource,
          mapOptions: (employee: EmployeeFilterNode) => ({
            label: employee.fullName ?? '',
            value: employee.id,
          }),
        },
      },
      createdAt: {
        type: 'date',
        label: i18n.t('trainings.training_requests.requests.datatable.filters.date'),
      },
      competencyId: {
        type: 'in',
        label: i18n.t('trainings.trainings_table.competencies'),
        options: {
          cache: true,
          options: async () => {
            const { data } = await client.query({
              query: GetCompetenciesDocument,
            })
            const competencies = data?.competencies?.competencies ?? []
            return competencies.map((c) => ({
              label: c.name,
              value: c.id,
            }))
          },
        },
      },
      status: {
        type: 'in',
        label: i18n.t('trainings.training_requests.requests.datatable.status'),
        options: {
          options: [
            {
              label: i18n.t('trainings.training_requests.requests.datatable.pending'),
              value: 'review',
            },
            {
              label: i18n.t('trainings.training_requests.requests.datatable.approved'),
              value: 'approved',
            },
            {
              label: i18n.t('trainings.training_requests.requests.datatable.rejected'),
              value: 'rejected',
            },
          ],
        },
      },
    },
    itemUrl: (item) =>
      `${pathname}#${encodeActivity('trainings.trainingRequest', { id: String(item.id) })}`,
    itemActions,
    secondaryActions,
  })

  const getNoDataEmptyState = () => {
    if (!settingsEnabled) {
      return {
        title: i18n.t('trainings.training_requests.requests.empty_state.title'),
        description: i18n.t('trainings.training_requests.requests.empty_state.description'),
        actions: [
          {
            label: i18n.t('trainings.training_requests.requests.empty_state.cta'),
            onClick: () => navigate(navigation.settings.trainings.fullPath()),
            variant: 'default' as const,
          },
        ],
      }
    }

    if (canCreate) {
      return {
        title: i18n.t('trainings.training_requests.my_requests.empty_state.title'),
        actions: [
          {
            label: i18n.t('trainings.training_requests.my_requests.empty_state.cta'),
            onClick: () => navigate(navigation.myTrainings.requests.list.new.fullPath()),
            variant: 'default' as const,
          },
        ],
      }
    }

    return {
      title: i18n.t('trainings.training_requests.my_requests.empty_state.title_no_permissions'),
      description: i18n.t(
        'trainings.training_requests.my_requests.empty_state.description_no_permissions'
      ),
    }
  }

  return (
    <OneDataCollection
      id="trainings/requests/v1"
      storage={DEFAULT_DATA_COLLECTION_STORAGE}
      source={source}
      emptyStates={{
        'no-data': getNoDataEmptyState(),
      }}
      visualizations={getVisualizations()}
    />
  )
}
